import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    getReleaseDetail,
    getReleaseFeed,
    getReleaseUnreadCount,
    markAllReleasesRead,
} from 'services/releaseNotesService';
import './ReleaseNotesModal.css';

const RELEASE_FEED_LIMIT = 8;
const MAX_VISIBLE_RELEASES = 4;
const DISMISSAL_STORAGE_KEY = 'shiftwareReleaseNotesDismissedSignature';
const PREVIEW_QUERY_PARAM = 'releaseNotesPreview';
const DETAIL_STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
};

const previewReleases = [
    {
        id: 'preview-routing',
        slug: 'preview-routing',
        version: 'v2.4.0',
        title: 'Smarter routing & route insights',
        summary: 'Plan efficient routes and compare alternatives with real-time surface insights.',
        releaseType: 'MINOR',
        releaseTypeLabel: 'Feature',
        publishedAt: '2026-05-08T10:00:00Z',
        isPinned: true,
        tags: [],
        itemCount: 2,
        primaryImage: '',
        isRead: false,
        items: [],
        attachments: [],
    },
    {
        id: 'preview-charging',
        slug: 'preview-charging',
        version: 'v2.3.0',
        title: 'Charging session improvements',
        summary: 'Faster filtering, clearer timelines, and new export options.',
        releaseType: 'MINOR',
        releaseTypeLabel: 'Improvement',
        publishedAt: '2026-04-22T10:00:00Z',
        isPinned: false,
        tags: [],
        itemCount: 1,
        primaryImage: '',
        isRead: false,
        items: [],
        attachments: [],
    },
    {
        id: 'preview-mobile',
        slug: 'preview-mobile',
        version: 'v2.2.1',
        title: 'Mobile app & bug fixes',
        summary: 'Smoother offline sync, stability improvements, and issues resolved.',
        releaseType: 'PATCH',
        releaseTypeLabel: 'Fix',
        publishedAt: '2026-04-02T10:00:00Z',
        isPinned: false,
        tags: [],
        itemCount: 1,
        primaryImage: '',
        isRead: false,
        items: [],
        attachments: [],
    },
    {
        id: 'preview-performance',
        slug: 'preview-performance',
        version: 'v2.2.0',
        title: 'Telemetry performance boost',
        summary: 'Faster data updates and improved dashboard responsiveness.',
        releaseType: 'MINOR',
        releaseTypeLabel: 'Performance',
        publishedAt: '2026-03-18T10:00:00Z',
        isPinned: false,
        tags: [],
        itemCount: 1,
        primaryImage: '',
        isRead: false,
        items: [],
        attachments: [],
    },
];

const previewRouteImage = `data:image/svg+xml;utf8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 200"><rect width="320" height="200" rx="10" fill="#f8fafc"/><path d="M35 145 C90 52 164 168 285 58" fill="none" stroke="#ec5600" stroke-width="8" stroke-linecap="round"/><path d="M42 52 C92 92 142 92 188 44 C230 4 258 56 286 86" fill="none" stroke="#4b8bbe" stroke-width="5" stroke-linecap="round"/><g fill="#ffffff" stroke="#2f3b4a" stroke-width="3"><circle cx="35" cy="145" r="10"/><circle cx="285" cy="58" r="10"/><circle cx="188" cy="44" r="8"/></g><text x="22" y="184" font-family="Arial" font-size="18" fill="#334155">Route comparison view</text></svg>'
)}`;

const previewDetailBySlug = {
    'preview-routing': {
        ...previewReleases[0],
        items: [
            {
                id: 'preview-routing-comparison',
                category: 'FEATURE',
                categoryLabel: 'New feature',
                title: 'See side-by-side route comparisons',
                description: 'Compare distance, time, and energy before dispatch.',
                sortOrder: 0,
            },
            {
                id: 'preview-routing-surface',
                category: 'FEATURE',
                categoryLabel: 'New feature',
                title: 'Live surface conditions included',
                description: 'Surface insights now contribute to route scoring.',
                sortOrder: 1,
            },
        ],
        attachments: [
            {
                id: 'preview-route-image',
                url: previewRouteImage,
                caption: 'Route comparison view',
                width: 320,
                height: 200,
                sortOrder: 0,
            },
        ],
    },
};

const defaultCopy = {
    title: "We've been busy building",
    subtitle: 'Here are the latest updates ready for your fleet team.',
    closeLabel: 'Close release notes',
    markAllRead: 'Mark all read',
    gotIt: 'Got it',
    footerNote: 'Only customer-facing updates are shown here.',
    unreadLabel: 'Unread',
    pinnedLabel: 'Pinned',
    loadingDetail: 'Loading release details...',
    detailUnavailable: 'Release details could not load.',
    actionFailed: 'Could not mark releases read. Please try again.',
    imageAlt: 'Release preview',
};

const getStorageValue = (key) => {
    if (typeof window === 'undefined') return '';
    try {
        return window.sessionStorage.getItem(key) || '';
    } catch {
        return '';
    }
};

const setStorageValue = (key, value) => {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // Session dismissal should never block the modal close path.
    }
};

const isTruthyPreviewValue = (value) => ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());

const isReleaseNotesPreviewEnabled = () => {
    if (process.env.NODE_ENV === 'production') return false;
    if (isTruthyPreviewValue(process.env.REACT_APP_RELEASE_NOTES_PREVIEW)) return true;
    if (typeof window === 'undefined') return false;

    try {
        const params = new URLSearchParams(window.location.search);
        return isTruthyPreviewValue(params.get(PREVIEW_QUERY_PARAM))
            || isTruthyPreviewValue(window.localStorage.getItem('releaseNotesPreview'));
    } catch {
        return false;
    }
};

const getVisibleReleases = (releases = []) => {
    const unreadReleases = releases.filter((release) => !release.isRead);
    const readReleases = releases.filter((release) => release.isRead);
    return [...unreadReleases, ...readReleases].slice(0, MAX_VISIBLE_RELEASES);
};

const getUnreadSignature = (releases = [], unreadCount = 0) => {
    const unreadSlugs = releases
        .filter((release) => !release.isRead && release.slug)
        .map((release) => release.slug);
    return `release-notes:v1:${unreadSlugs.length > 0 ? unreadSlugs.join('|') : `count:${unreadCount}`}`;
};

const formatDate = (value, locale) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    try {
        return new Intl.DateTimeFormat(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date);
    } catch {
        return date.toLocaleDateString();
    }
};

const getReleaseTone = (release = {}) => {
    const value = String([
        release.releaseTypeLabel,
        release.releaseType,
        release.tags?.[0]?.slug,
    ].filter(Boolean).join(' ')).toLowerCase();
    if (value.includes('feature') || value.includes('major')) return 'feature';
    if (value.includes('fix') || value.includes('hotfix')) return 'fix';
    if (value.includes('performance')) return 'performance';
    if (value.includes('security')) return 'security';
    if (value.includes('improvement')) return 'improvement';
    if (value.includes('announcement')) return 'announcement';
    return 'feature';
};

const ReleaseIcon = ({ tone }) => {
    if (tone === 'fix') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14.6 5.4a4.4 4.4 0 0 0 4.2 5.9l-7.5 7.5a3.2 3.2 0 0 1-4.5-4.5l7.8-7.8Z" />
                <path d="m5.3 18.7-1.6 1.6" />
            </svg>
        );
    }

    if (tone === 'performance') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 15a7 7 0 0 1 14 0" />
                <path d="M12 15l3-5" />
                <path d="M7.5 15h9" />
            </svg>
        );
    }

    if (tone === 'security') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3 5.5 5.5v5.4c0 4.1 2.5 7.6 6.5 9.1 4-1.5 6.5-5 6.5-9.1V5.5L12 3Z" />
                <path d="m9.5 12 1.7 1.7 3.6-4" />
            </svg>
        );
    }

    if (tone === 'improvement') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 18V9" />
                <path d="M12 18V5" />
                <path d="M18 18v-6" />
                <path d="M4 18h16" />
            </svg>
        );
    }

    if (tone === 'announcement') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 13h3l8 4V7l-8 4H5v2Z" />
                <path d="M8 13v5" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7.4 5.5a3 3 0 0 0-3 3c0 2.4 3 5.5 3 5.5s3-3.1 3-5.5a3 3 0 0 0-3-3Z" />
            <path d="M16.8 4.2a3 3 0 0 0-3 3c0 2.4 3 5.5 3 5.5s3-3.1 3-5.5a3 3 0 0 0-3-3Z" />
            <path d="M8.2 17.4c1.4-1.2 3.6-1.2 5 0s3.6 1.2 5 0" />
            <path d="M5.4 20h13.2" />
        </svg>
    );
};

const HeaderIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 4v16" />
        <path d="M5 9h14v10H5z" />
        <path d="M7 9c-1.4-2.7.2-4.4 2.2-3.2C10.5 6.6 12 9 12 9s1.5-2.4 2.8-3.2C16.8 4.6 18.4 6.3 17 9" />
    </svg>
);

const ChevronIcon = ({ expanded }) => (
    <svg viewBox="0 0 20 20" aria-hidden="true" className={expanded ? 'expanded' : ''}>
        <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
    </svg>
);

const CloseIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M5 5l10 10" />
        <path d="M15 5 5 15" />
    </svg>
);

const InfoIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="7.25" />
        <path d="M10 9.25v4" />
        <path d="M10 6.4h.01" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="m4.5 10.5 3.2 3.2 7.8-8" />
    </svg>
);

export default function ReleaseNotesModal({ token, copy = {}, locale = 'en-US' }) {
    const modalCopy = useMemo(() => ({ ...defaultCopy, ...(copy || {}) }), [copy]);
    const isPreviewMode = useMemo(() => isReleaseNotesPreviewEnabled(), []);
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);
    const previouslyFocusedElementRef = useRef(null);
    const requestedDetailSlugsRef = useRef(new Set());
    const [isOpen, setIsOpen] = useState(false);
    const [releases, setReleases] = useState([]);
    const [dismissalSignature, setDismissalSignature] = useState('');
    const [activeSlug, setActiveSlug] = useState('');
    const [detailsBySlug, setDetailsBySlug] = useState({});
    const [actionStatus, setActionStatus] = useState('idle');
    const [actionError, setActionError] = useState('');

    const visibleReleases = useMemo(() => getVisibleReleases(releases), [releases]);

    useEffect(() => {
        requestedDetailSlugsRef.current = new Set();
        setDetailsBySlug({});
    }, [isPreviewMode, token]);

    const closeForSession = useCallback(() => {
        if (dismissalSignature) {
            setStorageValue(DISMISSAL_STORAGE_KEY, dismissalSignature);
        }
        setIsOpen(false);
    }, [dismissalSignature]);

    useEffect(() => {
        let isMounted = true;

        if (isPreviewMode) {
            const nextVisibleReleases = getVisibleReleases(previewReleases);
            previouslyFocusedElementRef.current = document.activeElement;
            setReleases(previewReleases);
            setDismissalSignature('release-notes-preview:v1');
            setActiveSlug(nextVisibleReleases[0]?.slug || '');
            setActionStatus('idle');
            setActionError('');
            setIsOpen(true);
            return () => {
                isMounted = false;
            };
        }

        if (!token) {
            setIsOpen(false);
            return () => {
                isMounted = false;
            };
        }

        getReleaseUnreadCount({ token })
            .then((unreadCount) => {
                if (!isMounted || unreadCount < 1) return null;
                return getReleaseFeed({ token, limit: RELEASE_FEED_LIMIT }).then((feed) => ({
                    feed,
                    unreadCount,
                }));
            })
            .then((result) => {
                if (!isMounted || !result) return;

                const nextReleases = result.feed.results || [];
                const nextVisibleReleases = getVisibleReleases(nextReleases);
                if (nextVisibleReleases.length < 1) return;

                const nextSignature = getUnreadSignature(nextReleases, result.unreadCount);
                if (getStorageValue(DISMISSAL_STORAGE_KEY) === nextSignature) return;

                previouslyFocusedElementRef.current = document.activeElement;
                setReleases(nextReleases);
                setDismissalSignature(nextSignature);
                setActiveSlug(nextVisibleReleases[0].slug);
                setActionStatus('idle');
                setActionError('');
                setIsOpen(true);
            })
            .catch(() => {
                if (isMounted) {
                    setIsOpen(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isPreviewMode, token]);

    useEffect(() => {
        if (!isOpen || !activeSlug || requestedDetailSlugsRef.current.has(activeSlug)) return undefined;

        if (isPreviewMode) {
            requestedDetailSlugsRef.current.add(activeSlug);
            setDetailsBySlug((currentDetails) => ({
                ...currentDetails,
                [activeSlug]: {
                    status: DETAIL_STATUS.READY,
                    release: previewDetailBySlug[activeSlug] || previewReleases.find((release) => release.slug === activeSlug) || null,
                },
            }));
            return undefined;
        }

        if (!token) return undefined;

        let isMounted = true;
        requestedDetailSlugsRef.current.add(activeSlug);
        setDetailsBySlug((currentDetails) => ({
            ...currentDetails,
            [activeSlug]: {
                status: DETAIL_STATUS.LOADING,
                release: null,
            },
        }));

        getReleaseDetail({ token, slug: activeSlug })
            .then((release) => {
                if (!isMounted) return;
                setDetailsBySlug((currentDetails) => ({
                    ...currentDetails,
                    [activeSlug]: {
                        status: DETAIL_STATUS.READY,
                        release,
                    },
                }));
            })
            .catch(() => {
                if (!isMounted) return;
                setDetailsBySlug((currentDetails) => ({
                    ...currentDetails,
                    [activeSlug]: {
                        status: DETAIL_STATUS.ERROR,
                        release: null,
                    },
                }));
            });

        return () => {
            isMounted = false;
        };
    }, [activeSlug, isOpen, isPreviewMode, token]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.setTimeout(() => {
            dialogRef.current?.focus();
        }, 0);

        return () => {
            document.body.style.overflow = previousOverflow;
            previouslyFocusedElementRef.current?.focus?.();
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeForSession();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) return;

            const focusableElements = Array.from(
                dialogRef.current.querySelectorAll(
                    'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
            );
            if (focusableElements.length < 1) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [closeForSession, isOpen]);

    const handleMarkAllRead = async () => {
        if (!token || actionStatus === 'submitting') return;

        if (isPreviewMode) {
            setIsOpen(false);
            return;
        }

        setActionStatus('submitting');
        setActionError('');
        try {
            await markAllReleasesRead({ token });
            if (dismissalSignature) {
                setStorageValue(DISMISSAL_STORAGE_KEY, dismissalSignature);
            }
            setActionStatus('idle');
            setIsOpen(false);
        } catch {
            setActionStatus('idle');
            setActionError(modalCopy.actionFailed);
        }
    };

    if (!isOpen || visibleReleases.length < 1) return null;

    return (
        <div className="release-notes-modal-backdrop" aria-hidden={!isOpen ? 'true' : undefined}>
            <section
                className="release-notes-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="release-notes-modal-title"
                aria-describedby="release-notes-modal-subtitle"
                ref={dialogRef}
                tabIndex={-1}
            >
                <div className="release-notes-modal-header">
                    <div className="release-notes-modal-glyph">
                        <HeaderIcon />
                    </div>
                    <div className="release-notes-modal-title-group">
                        <h2 id="release-notes-modal-title">{modalCopy.title}</h2>
                        <p id="release-notes-modal-subtitle">{modalCopy.subtitle}</p>
                    </div>
                    <button
                        type="button"
                        className="release-notes-close-button"
                        aria-label={modalCopy.closeLabel}
                        onClick={closeForSession}
                        ref={closeButtonRef}
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className="release-notes-list">
                    {visibleReleases.map((release) => {
                        const isExpanded = activeSlug === release.slug;
                        const detailState = detailsBySlug[release.slug];
                        const detailRelease = detailState?.release || release;
                        const tone = getReleaseTone(detailRelease);
                        const categoryLabel = detailRelease.releaseTypeLabel
                            || detailRelease.tags?.[0]?.name
                            || (detailRelease.isPinned ? modalCopy.pinnedLabel : '');
                        const releaseDate = formatDate(detailRelease.publishedAt, locale);
                        const previewAttachment = detailRelease.attachments?.[0];
                        const previewImage = previewAttachment?.url || detailRelease.primaryImage;
                        const previewCaption = previewAttachment?.caption || detailRelease.title;

                        return (
                            <article
                                className={`release-notes-item ${isExpanded ? 'expanded' : ''} ${release.isRead ? 'read' : 'unread'} tone-${tone}`}
                                key={release.slug || release.id}
                            >
                                <button
                                    type="button"
                                    className="release-notes-item-button"
                                    aria-expanded={isExpanded}
                                    onClick={() => setActiveSlug(release.slug)}
                                >
                                    <span className="release-notes-item-icon">
                                        <ReleaseIcon tone={tone} />
                                    </span>
                                    <span className="release-notes-item-copy">
                                        <span className="release-notes-item-title-row">
                                            <span className="release-notes-item-title">{release.title}</span>
                                            {categoryLabel && (
                                                <span className="release-notes-category-chip">{categoryLabel}</span>
                                            )}
                                        </span>
                                        {release.summary && (
                                            <span className="release-notes-item-summary">{release.summary}</span>
                                        )}
                                    </span>
                                    <span className="release-notes-item-meta">
                                        {release.version && <span>{release.version}</span>}
                                        {release.version && releaseDate && <span aria-hidden="true">•</span>}
                                        {releaseDate && <span>{releaseDate}</span>}
                                        {!release.isRead && (
                                            <span className="release-notes-unread-dot">
                                                <span className="sr-only">{modalCopy.unreadLabel}</span>
                                            </span>
                                        )}
                                        <ChevronIcon expanded={isExpanded} />
                                    </span>
                                </button>

                                {isExpanded && (
                                    <div className="release-notes-detail">
                                        {detailState?.status === DETAIL_STATUS.LOADING && (
                                            <p className="release-notes-detail-state">{modalCopy.loadingDetail}</p>
                                        )}
                                        {detailState?.status === DETAIL_STATUS.ERROR && (
                                            <p className="release-notes-detail-state">{modalCopy.detailUnavailable}</p>
                                        )}
                                        {detailState?.status !== DETAIL_STATUS.LOADING && detailState?.status !== DETAIL_STATUS.ERROR && (
                                            <>
                                                {detailRelease.items?.length > 0 && (
                                                    <ul className="release-notes-change-list">
                                                        {detailRelease.items.slice(0, 3).map((item) => (
                                                            <li key={item.id || `${release.slug}-${item.title}`}>
                                                                <CheckIcon />
                                                                <span>
                                                                    <strong>{item.title}</strong>
                                                                    {item.description && <small>{item.description}</small>}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {previewImage && (
                                                    <figure className="release-notes-preview">
                                                        <img
                                                            src={previewImage}
                                                            alt={previewCaption || modalCopy.imageAlt}
                                                            width={previewAttachment?.width || undefined}
                                                            height={previewAttachment?.height || undefined}
                                                            decoding="async"
                                                        />
                                                        {previewCaption && <figcaption>{previewCaption}</figcaption>}
                                                    </figure>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>

                <footer className="release-notes-modal-footer">
                    <div className="release-notes-footer-note">
                        <InfoIcon />
                        <span>{modalCopy.footerNote}</span>
                    </div>
                    <div className="release-notes-footer-actions">
                        {actionError && <span className="release-notes-action-error" role="alert">{actionError}</span>}
                        <button
                            type="button"
                            className="release-notes-secondary-button"
                            onClick={handleMarkAllRead}
                            disabled={actionStatus === 'submitting'}
                        >
                            {modalCopy.markAllRead}
                        </button>
                        <button
                            type="button"
                            className="release-notes-primary-button"
                            onClick={closeForSession}
                            disabled={actionStatus === 'submitting'}
                        >
                            {modalCopy.gotIt}
                        </button>
                    </div>
                </footer>
            </section>
        </div>
    );
}
